import { ReactNode, useState } from 'react'

import { useNavigate, useRouter } from '@tanstack/react-router'
import { CancelEsignLinkDocumentDialog } from 'bank-onboarding-documents/src/components/CancelEsignLinkDocumentDialog'
import { useDeleteDocument } from 'bank-onboarding-documents/src/hooks'
import { useSendOnboardingDocument } from 'commons/hooks/bank/onboardingSetupDocuments'
import { Request } from 'commons/utils/request'
import { DownloadLink, TableActions, Toast } from 'ui/components'
import { TableButton } from 'ui/components/Buttons'

import { handlerServerError } from '@orbit/errors'

import { ActionsProps } from './Actions.types'

export const Actions = ({ item, applicationId }: ActionsProps): ReactNode => {
  const router = useRouter()
  const navigate = useNavigate()

  const { id, canCancel, canDownload, canEdit, canSend, canDelete } = item

  const [showCancelEsignDialog, setShowCancelEsignDialog] = useState(false)

  const { openDeleteConfirmation } = useDeleteDocument(id, () => router.invalidate())

  const { routine: sendOnboardingDocument } = useSendOnboardingDocument({
    onSuccess: async () => {
      Toast.successSmall(`The set of onboarding documents ID-${id} has been sent`)
      router.invalidate()
    },
    onError: (e) => handlerServerError(e)
  })

  let tableActionsComponent: ReactNode

  if (canDownload)
    tableActionsComponent = (
      <DownloadLink
        direct
        baseUrl={Request.urls.bank.downloadOnboardingDocuments}
        parametersForm={{ appDocusignEnvelopeId: id }}
        handleError={(error) => Toast.errorSmall(error)}
        withIcon={false}
      >
        <TableButton>Download</TableButton>
      </DownloadLink>
    )
  else
    tableActionsComponent = (
      <TableActions
        items={[
          ...(canSend
            ? [
                {
                  type: 'option' as const,
                  title: 'Send',
                  onClick: async () => await sendOnboardingDocument(id)
                }
              ]
            : []),
          ...(canEdit
            ? [
                {
                  type: 'edit' as const,
                  onClick: () =>
                    navigate({
                      to: '/application/$applicationId/onboarding-documents/$id/edit',
                      params: { applicationId: applicationId.toString(), id: id.toString() }
                    })
                }
              ]
            : []),
          ...(canCancel
            ? [
                {
                  type: 'option' as const,
                  title: 'Cancel eSing Link',
                  onClick: () => setShowCancelEsignDialog(true)
                }
              ]
            : []),
          ...(canDelete
            ? [
                {
                  type: 'delete' as const,
                  onClick: () => openDeleteConfirmation()
                }
              ]
            : [])
        ]}
      />
    )

  return (
    <>
      {canCancel && showCancelEsignDialog && (
        <CancelEsignLinkDocumentDialog
          id={id}
          onClose={() => setShowCancelEsignDialog(false)}
          onSuccess={() => router.invalidate()}
        />
      )}
      {tableActionsComponent}
    </>
  )
}
